import { useAuth } from '@clerk/clerk-react';
import { useEffect, useRef } from 'react';
import { setClerkJWT, setJWT, setActiveProfileId, clearAuthState, setTokenRefreshCallback, post as apiPost, get as apiGet } from './apiService';
import { setActiveProfile, setActiveTeam, patch as patchProfile, getById as getProfileById } from './profileService';
import { loadTeamFromProfile } from './teamService';
import { setLogLevels } from './loggerService';
import { LoginResponse } from '../types/loginResponse';
import { User } from '../types/user';

export function IsAuthenticated() {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const wasSignedIn = useRef(false);

  useEffect(() => {
    const handleClerkAuth = async () => {
      if (isLoaded && isSignedIn) {
        let skipCache = false;
        const isLocal = import.meta.env.VITE_ENV === 'local';
        // Force a fresh token on mount/reload to avoid stale JWT issues during development
        if (isLocal) skipCache = true;
        const token = await getToken({ skipCache });
        wasSignedIn.current = true;

        if (token) {
          setClerkJWT(token);
          // Exchange Clerk JWT for API JWT
          await loginWithExtJWT();
        }
      }

      // Only clear auth state if user was previously signed in via Clerk and then signed out
      // This prevents clearing JWT set by loginWithSecret for invitation flow
      if (isLoaded && !isSignedIn && wasSignedIn.current) {
        wasSignedIn.current = false;
        clearAuthState();
        setTokenRefreshCallback(null);
      }
    };

    handleClerkAuth();
  }, [isSignedIn, isLoaded, getToken]);

  // Register token refresh callback for automatic retry on 401 errors (only in local development)
  useEffect(() => {
    const isLocal = import.meta.env.VITE_ENV === 'local';

    if (isSignedIn && isLoaded && isLocal) {
      const refreshToken = async () => {
        const token = await getToken({ skipCache: true });
        if (token) {
          setClerkJWT(token);
          await loginWithExtJWT();
        }
      };
      setTokenRefreshCallback(refreshToken);
    } else {
      setTokenRefreshCallback(null);
    }

    return () => {
      setTokenRefreshCallback(null);
    };
  }, [isSignedIn, isLoaded, getToken]);

  return null; // This component doesn't render anything
}

// Logout helper - clears active profile, team, and auth state
// Optionally signs out of Clerk with specified signOut function
export async function logout(clerkSignOut?: () => Promise<void>): Promise<void> {
  // 1. Unset activeProfile and activeTeam
  setActiveProfile(null);
  setActiveTeam(null);

  // 2. Clear auth state (JWTs, active profile ID)
  clearAuthState();

  // 3. Logout of Clerk if signOut function is provided
  if (clerkSignOut) {
    await clerkSignOut();
  }
}

// Login with secret - authenticates via invitation link
// IMPORTANT: Caller should ensure user is logged out of Clerk before calling this
// (typically by checking Clerk session and calling logout with redirect if needed)
export async function loginWithSecret(
  secret: string
): Promise<LoginResponse> {
  return apiPost<LoginResponse>('/login-secret', {
    secret,
    renew: true,
  });
}

// Login with external JWT (Clerk) - exchanges Clerk JWT for API JWT
export async function loginWithExtJWT(): Promise<void> {
  const response = await apiGet<LoginResponse>('/login-ext-jwt');
  setJWT(response.jwt);
  await whoAmI();
}

// Get current user info - sets log levels and active profile
export async function whoAmI(profileId?: string): Promise<void> {
  const user = await apiGet<User>('/who-am-i');
  setLogLevels(user.logLevels);

  // Find profile by ID if provided, otherwise use default
  let activeProfileToSet = profileId
    ? user.profiles.find((p: { id: string }) => p.id === profileId)
    : user.profiles.find((p: { default: boolean }) => p.default);

  // If resolved profile is a 'company' persona, prefer the 'business' persona profile
  if (activeProfileToSet?.persona === 'company') {
    const businessProfile = user.profiles.find((p: { persona: string }) => p.persona === 'business');
    if (businessProfile) {
      activeProfileToSet = businessProfile;
    } else {
      // No business profile available — company-only profiles cannot use this app
      clearAuthState();
      return;
    }
  }

  // A profile with a businessId is required for a valid auth
  if (!activeProfileToSet?.businessId) {
    clearAuthState();
    return;
  }

  if (activeProfileToSet) {
    setActiveProfileId(activeProfileToSet.id);

    // Fetch full profile data
    const profile = await getProfileById(activeProfileToSet.id);
    setActiveProfile(profile);

    // Load team from profile's businessId
    await loadTeamFromProfile();

    // Check if timezone is set, if not set it to browser timezone
    if (!profile.timezone) {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      profile.timezone = browserTimezone;

      // Update the profile with the timezone
      await patchProfile({
        id: profile.id,
        timezone: browserTimezone,
      });
    }
  }

  return;
}

// Convenience export
export const authService = {
  logout,
  loginWithSecret,
  loginWithExtJWT,
  whoAmI,
};
